import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { IconRefresh } from '@tabler/icons-react';
import { DesktopLayout } from 'src/layout';
import AcquisitionAnalyticsService, {
    AcquisitionCoverage,
    AcquisitionFilters,
    AcquisitionMoneyRow,
    AcquisitionProduct,
    AcquisitionJourneyReport,
    AcquisitionJourneyStep,
    AcquisitionReport,
    AcquisitionSourceStatus,
    buildReportParams,
} from 'src/services/AcquisitionAnalyticsService';

type AccessState = 'loading' | 'allowed' | 'forbidden' | 'unavailable' | 'error';
type ReportState = 'idle' | 'loading' | 'ready' | 'error' | 'forbidden' | 'unavailable';

const PRODUCT_LABELS: Record<AcquisitionProduct, string> = {
    lead_package: 'Пакет лидов',
    lead_year_unlimited: 'Лиды на год',
    user_subscription: 'Подписка пользователя',
    project_subscription: 'Подписка проекта',
    agent_dialog_package: 'Диалоги агента',
    agent_wallet_topup: 'Пополнение агента',
};
const STEP_LABELS: Record<string, string> = {
    touch_captured: 'Касания',
    product_viewed: 'Просмотры продукта',
    registration_clicked: 'Переходы к регистрации',
    registration_started: 'Начатые регистрации',
    account_created: 'Созданные аккаунты',
    email_confirmed: 'Подтверждения email',
    setup_completed: 'Настроили продукт',
    order_created: 'Созданные заказы',
    product_purchase: 'Покупки',
    external_cash: 'Внешние поступления',
};
const SURFACE_LABELS: Record<string, string> = {
    leads: 'Лиды',
    'lead-streams': 'Потоки лидов',
    'ai-managers': 'ИИ-менеджеры',
    integrations: 'Интеграции',
    partnership: 'Партнёрство',
    'telegram-subscribers': 'Подписчики Telegram',
    creatives: 'Креативы',
    'knowledge-base': 'База знаний',
    commenting: 'Комментирование',
    'sales-funnel': 'Воронка продаж',
    notifications: 'Уведомления',
    registration: 'Регистрация',
    site: 'Сайт',
};
const COVERAGE_LABELS: Record<string, string> = {
    financial: 'Финансовые события',
    lifecycle: 'Регистрации и подтверждения',
    touches: 'Касания',
    browser_observations: 'Наблюдения браузера',
    orders: 'Заказы',
    product_funnel: 'Настройка продукта',
    metrica: 'Яндекс Метрика',
    refund_registry: 'Возвраты',
    direct_cost: 'Расходы Директа',
    writer_scope: 'Платёжные источники',
    product_mapping: 'Связь страниц с продуктами',
};
const STATUS_LABELS: Record<AcquisitionSourceStatus, string> = {
    complete: 'Полное',
    partial: 'Частичное',
    pending: 'Обновляется',
    error: 'Ошибка',
    not_connected: 'Не подключено',
};
const REASON_LABELS: Record<string, string> = {
    yandex_direct_cost_not_connected: 'Расходы Директа не подключены',
    direct_cost_not_connected: 'Нет данных о расходах',
    direct_cost_and_new_buyer_history_not_connected: 'Нет расходов и полной истории новых покупателей',
    cost_and_complete_refund_coverage_not_connected: 'Нет расходов и полного реестра возвратов',
    refund_registry_incomplete: 'Реестр возвратов неполный',
    refund_registry_not_connected: 'Реестр возвратов не подключён',
    registry_coverage_is_partial: 'Возвраты покрыты частично',
    registry_verified_through_observation_end: 'Возвраты сверены до конца окна',
    metrica_daily_source_not_connected: 'Метрика не подключена',
    metrica_daily_source_error: 'Ошибка загрузки Метрики',
    metrica_daily_source_pending: 'Метрика обновляется',
    unsupported_filter: 'Источник не поддерживает выбранный фильтр',
    missing_partial_or_sampled_days: 'Есть пропуски, неполные или сэмплированные дни',
    all_daily_visits_complete: 'Все дневные визиты загружены',
    daily_unique_visitors_are_not_additive: 'Дневные посетители не складываются в уникальных за период',
    conversion_maturity_window_not_approved: 'Окно зрелости конверсии не определено',
    only_lead_package_writers_connected_no_historical_backfill: 'Подключены не все покупки, истории до запуска нет',
    canonical_auth_hooks_active_without_historical_backfill: 'Аккаунты учитываются без исторической загрузки',
    server_capture_active_without_browser_rollout_or_historical_backfill:
        'Серверные касания учитываются без полной браузерной и исторической загрузки',
    browser_observation_capture_partial_no_historical_backfill:
        'Наблюдения браузера собираются частично, без исторической загрузки',
    canonical_product_setup_partial_no_historical_backfill:
        'Настройка продукта собирается частично, без исторической загрузки',
    canonical_initial_writers_partial_no_historical_backfill: 'Подключены начальные операции без исторической загрузки',
    canonical_order_writers_partial_no_historical_backfill:
        'Заказы собираются из подключённых checkout без исторической загрузки',
    canonical_initial_operations_only_no_historical_backfill:
        'Подключены начальные покупки и пополнение кошелька; другие операции не покрыты',
    browser_surface_not_mapped_to_financial_product: 'Страница продукта не подтверждает финансовую категорию покупки',
    product_mapping_unavailable: 'Для этого шага нет подтверждённой связи с продуктом',
    strict_observed_sequence_without_maturity_window:
        'Показана только полностью наблюдаемая последовательность; окно зрелости не задано',
    returning_requires_earlier_canonical_purchase:
        'Повторная покупка подтверждается только более ранней канонической покупкой',
    first_observed_not_first_ever: 'Первый наблюдаемый заказ может быть не первым за всю историю аккаунта',
    direct_cost_source_not_configured: 'Источник расходов Директа не настроен',
    direct_cost_exact_period_complete: 'Получен полный отчёт о расходах за точный период',
    direct_cost_exact_period_missing: 'Нет отчёта о расходах за точный период',
    direct_cost_snapshot_stale: 'Снимок расходов Директа устарел',
    direct_cost_snapshot_incomplete: 'Снимок расходов Директа неполный',
    direct_cost_provisional_period: 'Период ещё не завершён; подтверждённый расход пока недоступен',
    campaign_mapping_incomplete: 'Не для всех кампаний настроена подтверждённая связь',
    product_cost_allocation_unknown: 'Расход кампании не распределён по продуктам',
    mixed_campaign_product_allocation_unknown: 'Расход смешанной кампании нельзя точно распределить по продуктам',
    unsupported_cohort_cost: 'Расход календарного периода не является расходом когорты',
    purchase_goal_attribution_and_maturity_not_verified: 'Атрибуция покупки и зрелость конверсии не подтверждены',
    new_buyer_history_incomplete: 'Нет полной истории новых покупателей',
    cost_and_revenue_attribution_scopes_not_aligned: 'Области атрибуции расходов и выручки не согласованы',
    source_not_connected: 'Источник не подключён',
    subscriptions_agent_dialogs_wallet_topups_not_connected: 'Подписки и платежи агента пока не подключены',
};

const cardSx = {
    p: { xs: 2, sm: 2.5 },
    border: '1px solid var(--lm-border)',
    borderRadius: '10px',
    bgcolor: 'var(--lm-surface)',
    boxShadow: 'none',
} as const;

function shiftDate(value: string, days: number) {
    const date = new Date(`${value}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

function moscowToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Moscow',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${value.year}-${value.month}-${value.day}`;
}

export function initialAcquisitionFilters(now = new Date()): AcquisitionFilters {
    const today = moscowToday(now);
    return { dateFrom: shiftDate(today, -29), dateToInclusive: today, mode: 'event' };
}

export function formatMoney(value: string | null, currency: string) {
    if (value === null) return '—';
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
    if (!match) return '—';
    const integer = match[2].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    const fraction = (match[3] || '').padEnd(2, '0').slice(0, 2);
    return `${match[1]}${integer},${fraction}\u00a0${currency}`;
}

export function formatDirectCost(value: string | null, currency: string | null) {
    if (value === null || !currency) return '—';
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
    if (!match) return '—';
    const integer = match[2].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    const fraction = (match[3] || '').padEnd(2, '0');
    return `${match[1]}${integer},${fraction}\u00a0${currency}`;
}

export interface AcquisitionAnalyticsClient {
    getCapabilities(): Promise<{ own_analytics: boolean; journeys: boolean }>;
    getReport(filters: AcquisitionFilters): Promise<AcquisitionReport>;
    getJourneys?(filters: AcquisitionFilters): Promise<AcquisitionJourneyReport>;
}

function httpStatus(error: unknown) {
    return (error as { response?: { status?: number } })?.response?.status;
}

function reason(value: string | null | undefined) {
    if (!value) return '';
    return REASON_LABELS[value] || 'Причина не уточнена';
}

function shortDateTime(value: string | null) {
    if (!value) return '—';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}.${month}.${year}`;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? 'Дата не уточнена'
        : date.toLocaleString('ru-RU', {
              dateStyle: 'short',
              timeStyle: 'short',
              timeZone: 'Europe/Moscow',
          });
}

function CoverageTable({ rows }: { rows: Record<string, AcquisitionCoverage> }) {
    return (
        <TableContainer>
            <Table
                size="small"
                aria-label="Покрытие источников"
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Источник</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Покрытие</TableCell>
                        <TableCell>Обновлено</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(rows).map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell>{COVERAGE_LABELS[key] || 'Другой источник'}</TableCell>
                            <TableCell>{STATUS_LABELS[value.status]}</TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {value.covered_from || value.covered_to
                                        ? `${shortDateTime(value.covered_from)} — ${shortDateTime(value.covered_to)}`
                                        : '—'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {reason(value.reason)}
                                </Typography>
                            </TableCell>
                            <TableCell>{shortDateTime(value.fetched_at)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function MoneyTable({ rows }: { rows: AcquisitionMoneyRow[] }) {
    return (
        <TableContainer>
            <Table
                size="small"
                aria-label="Деньги по валютам"
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Валюта</TableCell>
                        <TableCell align="right">Покупки</TableCell>
                        <TableCell align="right">Покупатели</TableCell>
                        <TableCell align="right">Оплачено</TableCell>
                        <TableCell align="right">Внешние поступления</TableCell>
                        <TableCell align="right">Кошелёк</TableCell>
                        <TableCell align="right">Бонусы</TableCell>
                        <TableCell align="right">Возвраты</TableCell>
                        <TableCell align="right">Нетто</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.currency}>
                            <TableCell>{row.currency}</TableCell>
                            <TableCell align="right">{row.purchases.operations}</TableCell>
                            <TableCell align="right">
                                {row.purchases.buyers_complete ? row.purchases.buyers : '—'}
                            </TableCell>
                            <TableCell align="right">{formatMoney(row.purchases.paid_value, row.currency)}</TableCell>
                            <TableCell align="right">
                                {formatMoney(row.cash_in.external_cash_value, row.currency)}
                            </TableCell>
                            <TableCell align="right">{formatMoney(row.purchases.wallet_value, row.currency)}</TableCell>
                            <TableCell align="right">{formatMoney(row.purchases.bonus_value, row.currency)}</TableCell>
                            <TableCell align="right">{formatMoney(row.refunds.observed_value, row.currency)}</TableCell>
                            <TableCell
                                align="right"
                                title={reason(row.refunds.net_reason)}
                            >
                                <Typography variant="body2">
                                    {formatMoney(row.refunds.net_value, row.currency)}
                                </Typography>
                                {row.refunds.net_value === null && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {reason(row.refunds.net_reason)}
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function JourneyTable({ report }: { report: AcquisitionJourneyReport }) {
    const stage = (value: AcquisitionJourneyStep) => (
        <Stack spacing={0.25}>
            <Typography variant="body2">
                {value.status === 'observed'
                    ? value.occurred_at
                        ? shortDateTime(value.occurred_at)
                        : 'Есть, время неизвестно'
                    : 'Не подтверждено'}
            </Typography>
            {value.status === 'observed' && (
                <Typography variant="caption" color="text.secondary">
                    {value.evidence.includes('native') ? 'Нативное состояние' : 'Серверное событие'}
                </Typography>
            )}
        </Stack>
    );
    const touchRelation = (value: 'pre_registration' | 'post_registration' | 'unknown') => {
        if (value === 'pre_registration') return 'До регистрации';
        if (value === 'post_registration') return 'После регистрации';
        return 'Время регистрации неизвестно';
    };
    return (
        <TableContainer>
            <Table size="small" aria-label="Цепочки по аккаунтам">
                <TableHead>
                    <TableRow>
                        <TableCell>Аккаунт</TableCell>
                        <TableCell>Продукт</TableCell>
                        <TableCell>Источник регистрации</TableCell>
                        <TableCell>Рекламные касания</TableCell>
                        <TableCell>Регистрация</TableCell>
                        <TableCell>Подтверждение</TableCell>
                        <TableCell>Настройка</TableCell>
                        <TableCell>Заказы</TableCell>
                        <TableCell>Оплаты</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {report.results.map((row) => (
                        <TableRow key={`${row.account_ref}:${row.product}`}>
                            <TableCell>{row.account_ref}</TableCell>
                            <TableCell>{row.product === 'unknown' ? 'Не определён' : row.product}</TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {row.campaign.status === 'observed'
                                        ? `${row.campaign.campaign}${row.campaign.ad ? ` / ${row.campaign.ad}` : ''}`
                                        : 'Не связана'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {row.binding.status === 'observed' ? 'Связь с аккаунтом подтверждена' : 'Связь неизвестна'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                {(row.touches ?? []).length === 0
                                    ? 'Нет'
                                    : (row.touches ?? []).map((touch) => (
                                          <Stack
                                              key={`${touch.occurred_at}:${touch.campaign}:${touch.ad}`}
                                              spacing={0.25}
                                              mb={0.5}
                                          >
                                              <Typography variant="body2">
                                                  {touch.campaign || 'Кампания не указана'}
                                                  {touch.ad ? ` / ${touch.ad}` : ''}
                                              </Typography>
                                              <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                              >
                                                  {shortDateTime(touch.occurred_at)} ·{' '}
                                                  {touchRelation(touch.registration_relation)}
                                              </Typography>
                                          </Stack>
                                      ))}
                            </TableCell>
                            <TableCell>{stage(row.registration)}</TableCell>
                            <TableCell>{stage(row.confirmation)}</TableCell>
                            <TableCell>{stage(row.setup)}</TableCell>
                            <TableCell>
                                {row.orders.length === 0
                                    ? 'Нет'
                                    : row.orders.map((order) => (
                                          <Typography key={`${order.product}:${order.occurred_at}`} variant="body2">
                                              {shortDateTime(order.occurred_at)} · {order.product}
                                          </Typography>
                                      ))}
                            </TableCell>
                            <TableCell>
                                <Stack spacing={0.5}>
                                    <Typography variant="body2">
                                        Провайдер: {row.payments.provider_verified.length}
                                    </Typography>
                                    {row.payments.provider_verified.map((payment) => (
                                        <Typography
                                            key={`provider:${payment.product}:${payment.occurred_at}`}
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {shortDateTime(payment.occurred_at)} · {formatMoney(payment.amount, payment.currency)}
                                        </Typography>
                                    ))}
                                    <Typography variant="body2">
                                        Нативные, не проверены: {row.payments.native_paid_unverified.length}
                                    </Typography>
                                    {row.payments.native_paid_unverified.map((payment) => (
                                        <Typography
                                            key={`native:${payment.product}:${payment.occurred_at}`}
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {shortDateTime(payment.occurred_at)} · {formatMoney(payment.amount, payment.currency)}
                                        </Typography>
                                    ))}
                                    {row.payments.coverage === 'unknown' && (
                                        <Typography variant="caption" color="text.secondary">
                                            Покрытие неизвестно
                                        </Typography>
                                    )}
                                </Stack>
                            </TableCell>
                        </TableRow>
                    ))}
                    {report.results.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={9}>За выбранный период цепочек нет.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ProductTable({ rows }: { rows: AcquisitionReport['products'] }) {
    if (!rows.length) return null;
    return (
        <Box sx={{ mt: 2.5 }}>
            <Typography
                component="h3"
                fontWeight={700}
                mb={1}
            >
                По продуктам
            </Typography>
            <TableContainer>
                <Table
                    size="small"
                    aria-label="Покупки по продуктам"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Продукт</TableCell>
                            <TableCell>Валюта</TableCell>
                            <TableCell align="right">Покупки</TableCell>
                            <TableCell align="right">Покупатели</TableCell>
                            <TableCell align="right">Оплачено</TableCell>
                            <TableCell align="right">Внешние поступления</TableCell>
                            <TableCell align="right">Бонусы</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={`${row.product}:${row.currency}`}>
                                <TableCell>{PRODUCT_LABELS[row.product] || 'Продукт'}</TableCell>
                                <TableCell>{row.currency}</TableCell>
                                <TableCell align="right">{row.purchases.operations}</TableCell>
                                <TableCell align="right">
                                    {row.purchases.buyers_complete ? row.purchases.buyers : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    {formatMoney(row.purchases.paid_value, row.currency)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatMoney(row.cash_in.external_cash_value, row.currency)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatMoney(row.purchases.bonus_value, row.currency)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function count(value: number | null) {
    return value === null ? '—' : value.toLocaleString('ru-RU');
}

function ratio(value: string | null) {
    if (value === null || !/^\d+(?:\.\d+)?$/.test(value)) return '—';
    return `${(Number(value) * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

function StepTable({
    label,
    path,
}: {
    label: string;
    path: AcquisitionReport['linked_journey']['paths']['new_account'];
}) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                mb={1}
            >
                <Typography
                    component="h3"
                    fontWeight={700}
                >
                    {label}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    Цепочки: {count(path.eligible_journeys)}
                </Typography>
            </Stack>
            <TableContainer>
                <Table
                    size="small"
                    aria-label={`Связанный путь: ${label}`}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Этап</TableCell>
                            <TableCell align="right">События</TableCell>
                            <TableCell align="right">Уникальные</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {path.steps.map((step) => (
                            <TableRow key={step.key}>
                                <TableCell>
                                    <Typography variant="body2">{STEP_LABELS[step.key] || 'Этап'}</Typography>
                                    {step.reason && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {reason(step.reason)}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">{count(step.events)}</TableCell>
                                <TableCell align="right">{count(step.unique_entities)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {path.reason && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                >
                    {reason(path.reason)}
                </Typography>
            )}
        </Box>
    );
}

function BrowserObservationTable({ report }: { report: AcquisitionReport }) {
    const observations = report.browser_observations;
    if (!observations.rows.length && !observations.reason) return null;
    return (
        <Box sx={{ mt: 2 }}>
            <Typography
                component="h3"
                fontWeight={700}
                mb={1}
            >
                По страницам
            </Typography>
            {observations.rows.length ? (
                <TableContainer>
                    <Table
                        size="small"
                        aria-label="Наблюдения браузера по страницам"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>Событие</TableCell>
                                <TableCell>Страница</TableCell>
                                <TableCell align="right">События</TableCell>
                                <TableCell align="right">Уникальные</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {observations.rows.map((row) => (
                                <TableRow key={`${row.event_type}:${row.surface}`}>
                                    <TableCell>{STEP_LABELS[row.event_type] || 'Событие'}</TableCell>
                                    <TableCell>{SURFACE_LABELS[row.surface] || 'Другая страница'}</TableCell>
                                    <TableCell align="right">{row.events}</TableCell>
                                    <TableCell align="right">{row.unique_entities}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {reason(observations.reason)}
                </Typography>
            )}
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
            >
                Страница показывает место события и не определяет оплаченный продукт.
            </Typography>
        </Box>
    );
}

function OrderPurchaseTable({ report }: { report: AcquisitionReport }) {
    const linkage = report.linked_journey.order_purchase;
    const rows = [
        ['Заказы в выборке', linkage.observed_live_orders],
        ['Сопоставимые заказы', linkage.comparable_live_orders],
        ['Связанные покупки', linkage.linked],
        ['Покупки всего', linkage.total_purchases],
        ['Заказы без связанной покупки', linkage.orders_without_linked_purchase],
        ['Бесплатные заказы', linkage.free_orders],
        ['Покупки прежних источников без заказа', linkage.legacy_purchases_without_order],
        ['Несовпадение среды', linkage.environment_mismatches],
        ['Тестовые заказы исключены', linkage.test_orders_excluded],
        ['Неоднозначные связи', linkage.ambiguous_matches],
        ['Конфликтующие связи', linkage.conflicting_matches],
        ['Покупка раньше заказа', linkage.temporal_mismatches],
    ] as const;
    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                gap={0.5}
                mb={1}
            >
                <Typography
                    component="h3"
                    fontWeight={700}
                >
                    Связь заказа и покупки
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    Покрытие: {ratio(linkage.coverage)}
                </Typography>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1,
                }}
            >
                {rows.map(([label, value]) => (
                    <Box
                        key={label}
                        sx={{ py: 0.5, minWidth: 0 }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {label}
                        </Typography>
                        <Typography fontWeight={700}>{value.toLocaleString('ru-RU')}</Typography>
                    </Box>
                ))}
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
            >
                Заказ без связанной покупки — результат сопоставления, а не статус оплаты или отмены.
            </Typography>
        </Box>
    );
}

function ReportView({ report }: { report: AcquisitionReport }) {
    const empty =
        report.observed_steps.every((step) => step.events === 0) &&
        report.browser_observations.rows.length === 0 &&
        report.money.currencies.length === 0 &&
        report.metrica.daily_rows.length === 0;
    const partial = Object.values(report.coverage).some((source) => source.status !== 'complete');
    if (empty) {
        return (
            <Stack spacing={2.5}>
                <Alert severity="info">Данных за период нет.</Alert>
                <Paper
                    elevation={0}
                    sx={cardSx}
                >
                    <Typography
                        component="h2"
                        variant="h6"
                        fontWeight={700}
                        mb={1.5}
                    >
                        Покрытие и свежесть
                    </Typography>
                    <CoverageTable rows={report.coverage} />
                </Paper>
            </Stack>
        );
    }
    return (
        <Stack spacing={2.5}>
            {partial && <Alert severity="warning">Данные собраны частично. Проверьте покрытие источников.</Alert>}
            {report.request.filters.product && (
                <Alert severity="info">
                    Продукт фильтрует заказы и деньги. Ранние шаги без подтверждённой связи показываются как
                    неизвестные.
                </Alert>
            )}
            {(report.request.filters.channel || report.request.filters.campaign) && (
                <Alert severity="info">
                    У заказов и покупок используется атрибуция операции, у ранних шагов — последнее касание до события.
                    Значения разных шагов не складываются.
                </Alert>
            )}

            <Paper
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                    mb={1.5}
                >
                    Наблюдаемые события
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                        gap: 1,
                    }}
                >
                    {report.observed_steps.map((step) => (
                        <Box
                            key={step.key}
                            sx={{ py: 1, minWidth: 0 }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {STEP_LABELS[step.key] || 'Другой этап'}
                            </Typography>
                            <Typography
                                fontSize={24}
                                fontWeight={750}
                            >
                                {count(step.events)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Уникальные: {count(step.unique_entities)}
                            </Typography>
                            {step.reason && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    {reason(step.reason)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
                <BrowserObservationTable report={report} />
            </Paper>

            <Paper
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                >
                    Связанные пути
                </Typography>
                <Typography
                    color="text.secondary"
                    variant="body2"
                    mt={0.5}
                    mb={2}
                >
                    Последовательности одного известного аккаунта или анонимного посетителя.
                </Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={3}
                    mb={2}
                >
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Зрелость конверсии
                        </Typography>
                        <Typography fontWeight={600}>
                            {report.linked_journey.maturity_window.days === null
                                ? 'Не определена'
                                : `${report.linked_journey.maturity_window.days} дн.`}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Подтверждённые повторные заказы
                        </Typography>
                        <Typography fontWeight={600}>
                            {report.linked_journey.buyer_history.known_returning_orders.toLocaleString('ru-RU')}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Первые наблюдаемые заказы
                        </Typography>
                        <Typography fontWeight={600}>
                            {report.linked_journey.buyer_history.first_observed_orders.toLocaleString('ru-RU')}
                        </Typography>
                    </Box>
                </Stack>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={2}
                >
                    {reason(report.linked_journey.buyer_history.reason)}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(320px, 1fr)' },
                        gap: 2.5,
                    }}
                >
                    <StepTable
                        label="Новый аккаунт"
                        path={report.linked_journey.paths.new_account}
                    />
                    <StepTable
                        label="Повторная покупка"
                        path={report.linked_journey.paths.returning_buyer}
                    />
                </Box>
                <OrderPurchaseTable report={report} />
            </Paper>

            <Paper
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                    mb={1.5}
                >
                    Деньги
                </Typography>
                <Typography
                    color="text.secondary"
                    variant="body2"
                    mb={1.5}
                >
                    Оплачено — фактическая стоимость покупок. Внешние поступления, кошелёк и бонусы показаны отдельно.
                </Typography>
                {report.money.currencies.length > 1 && (
                    <Typography
                        color="text.secondary"
                        variant="body2"
                        mb={1.5}
                    >
                        Валюты показаны отдельно и не суммируются.
                    </Typography>
                )}
                {report.money.currencies.length ? (
                    <MoneyTable rows={report.money.currencies} />
                ) : (
                    <Typography color="text.secondary">Денежных фактов за период нет.</Typography>
                )}
                <ProductTable rows={report.products} />
            </Paper>

            <Paper
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                >
                    Визиты по дням
                </Typography>
                <Typography
                    color="text.secondary"
                    variant="body2"
                    mt={0.5}
                    mb={2}
                >
                    Сумма дневных визитов. Это не уникальные посетители за период.
                </Typography>
                <Typography
                    fontSize={26}
                    fontWeight={750}
                    title={reason(report.metrica.visits_reason)}
                >
                    {report.metrica.visits === null ? '—' : report.metrica.visits.toLocaleString('ru-RU')}
                </Typography>
                {report.metrica.daily_rows.length > 0 && (
                    <TableContainer sx={{ mt: 1.5 }}>
                        <Table
                            size="small"
                            aria-label="Дневные визиты"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell align="right">Визиты</TableCell>
                                    <TableCell>Статус</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.metrica.daily_rows.map((row) => (
                                    <TableRow key={`${row.date}:${row.model}`}>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell align="right">{row.visits === null ? '—' : row.visits}</TableCell>
                                        <TableCell>
                                            {STATUS_LABELS[row.completeness as AcquisitionSourceStatus] ||
                                                'Статус не уточнён'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Paper
                component="section"
                aria-label="Экономика"
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                    mb={1.5}
                >
                    Экономика
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 2 }}>
                    {(['direct_cost', 'cpa', 'cac', 'roas', 'roi'] as const).map((key) => (
                        <Box key={key}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {key === 'direct_cost' ? 'Расходы' : key.toUpperCase()}
                            </Typography>
                            <Typography
                                fontSize={20}
                                fontWeight={700}
                            >
                                {key === 'direct_cost'
                                    ? formatDirectCost(
                                          report.economics.direct_cost,
                                          report.economics.direct_cost_currency
                                      )
                                    : report.economics[key]?.replace('.', ',') ?? '—'}
                            </Typography>
                            {report.economics[key] === null && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {reason(report.economics[`${key}_reason`])}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
                {report.direct_cost_details && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={1.5}
                    >
                        Расходы относятся к календарному периоду {shortDateTime(report.direct_cost_details.period_from)}{' '}
                        — {shortDateTime(shiftDate(report.direct_cost_details.period_to_exclusive, -1))}. Кампании:{' '}
                        {report.direct_cost_details.selected_campaigns === null
                            ? '—'
                            : report.direct_cost_details.selected_campaigns.toLocaleString('ru-RU')}
                        .
                    </Typography>
                )}
            </Paper>

            <Paper
                elevation={0}
                sx={cardSx}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    fontWeight={700}
                    mb={1.5}
                >
                    Покрытие и свежесть
                </Typography>
                <CoverageTable rows={report.coverage} />
            </Paper>
        </Stack>
    );
}

export default function AcquisitionAnalytics({
    client = AcquisitionAnalyticsService,
    embedded = false,
}: {
    client?: AcquisitionAnalyticsClient;
    embedded?: boolean;
}) {
    const defaults = useMemo(() => initialAcquisitionFilters(), []);
    const [draft, setDraft] = useState<AcquisitionFilters>(defaults);
    const [applied, setApplied] = useState<AcquisitionFilters>(defaults);
    const [filterError, setFilterError] = useState('');
    const [access, setAccess] = useState<AccessState>('loading');
    const [reportAvailable, setReportAvailable] = useState(false);
    const [journeysAvailable, setJourneysAvailable] = useState(false);
    const [reportState, setReportState] = useState<ReportState>('idle');
    const [report, setReport] = useState<AcquisitionReport | null>(null);
    const [journeys, setJourneys] = useState<AcquisitionJourneyReport | null>(null);
    const [journeysState, setJourneysState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        let live = true;
        setAccess('loading');
        client
            .getCapabilities()
            .then((value) => {
                if (!live) return;
                setReportAvailable(value.own_analytics);
                setJourneysAvailable(value.journeys);
                setAccess(value.own_analytics || value.journeys ? 'allowed' : 'forbidden');
            })
            .catch((error) => live && setAccess(httpStatus(error) === 404 ? 'unavailable' : 'error'));
        return () => {
            live = false;
        };
    }, [client]);

    useEffect(() => {
        if (access !== 'allowed' || !reportAvailable) return;
        let live = true;
        setReportState('loading');
        client
            .getReport(applied)
            .then((value) => {
                if (!live) return;
                if (value.schema_version !== 2) throw new Error('Unsupported acquisition report schema');
                setReport(value);
                setReportState('ready');
            })
            .catch((error) => {
                if (!live) return;
                const status = httpStatus(error);
                setReportState(status === 403 ? 'forbidden' : status === 404 ? 'unavailable' : 'error');
            });
        return () => {
            live = false;
        };
    }, [access, reportAvailable, applied, retry, client]);

    const apply = () => {
        try {
            buildReportParams(draft);
        } catch (error) {
            setFilterError(error instanceof Error ? error.message : 'Проверьте фильтры');
            return;
        }
        setFilterError('');
        setApplied({ ...draft });
        setJourneys(null);
        setJourneysState('idle');
    };

    const loadJourneys = () => {
        if (!client.getJourneys) return;
        setJourneysState('loading');
        client.getJourneys(applied)
            .then((value) => {
                setJourneys(value);
                setJourneysState('ready');
            })
            .catch(() => setJourneysState('error'));
    };

    const content = (
        <Stack
            spacing={3}
            sx={{ px: { xs: 2, md: 0 }, py: { xs: 2, md: 0 }, minWidth: 0 }}
        >
            <Box>
                <Typography
                    component="h1"
                    sx={{ fontSize: { xs: 28, sm: 36 }, fontWeight: 800 }}
                >
                    Привлечение
                </Typography>
                <Typography
                    color="text.secondary"
                    mt={0.5}
                >
                    Собственные события, покупки и атрибуция.
                </Typography>
            </Box>

            {access === 'loading' && (
                <CircularProgress
                    size={24}
                    aria-label="Проверяем доступ"
                />
            )}
            {access === 'forbidden' && <Alert severity="info">Раздел доступен сотрудникам.</Alert>}
            {access === 'unavailable' && <Alert severity="info">Аналитика ещё не подключена.</Alert>}
            {access === 'error' && <Alert severity="error">Не удалось проверить доступ.</Alert>}

            {access === 'allowed' && (
                <>
                    <Paper
                        component="section"
                        elevation={0}
                        sx={cardSx}
                        aria-label="Фильтры отчёта"
                    >
                        <Typography
                            component="h2"
                            variant="h6"
                            fontWeight={700}
                            mb={2}
                        >
                            Окно наблюдения
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(6, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            <TextField
                                label="С"
                                type="date"
                                value={draft.dateFrom}
                                onChange={(event) => setDraft({ ...draft, dateFrom: event.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="По (включительно)"
                                type="date"
                                value={draft.dateToInclusive}
                                onChange={(event) => setDraft({ ...draft, dateToInclusive: event.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            {reportAvailable && (
                                <TextField
                                    select
                                    label="Режим"
                                    value={draft.mode}
                                    onChange={(event) =>
                                        setDraft({ ...draft, mode: event.target.value as AcquisitionFilters['mode'] })
                                    }
                                >
                                    <MenuItem value="event">События периода</MenuItem>
                                    <MenuItem value="cohort">Когорта</MenuItem>
                                </TextField>
                            )}
                            <TextField
                                select
                                label="Продукт"
                                value={draft.product || ''}
                                onChange={(event) =>
                                    setDraft({ ...draft, product: event.target.value as AcquisitionProduct | '' })
                                }
                            >
                                <MenuItem value="">Все продукты</MenuItem>
                                {Object.entries(PRODUCT_LABELS).map(([value, label]) => (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                    >
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {reportAvailable && (
                                <TextField
                                    label="Канал"
                                    value={draft.channel || ''}
                                    onChange={(event) => setDraft({ ...draft, channel: event.target.value })}
                                    inputProps={{ maxLength: 255 }}
                                />
                            )}
                            <TextField
                                label="Кампания"
                                value={draft.campaign || ''}
                                onChange={(event) => setDraft({ ...draft, campaign: event.target.value })}
                                inputProps={{ maxLength: 255 }}
                            />
                        </Box>
                        {filterError && (
                            <Typography
                                color="error"
                                variant="body2"
                                mt={1}
                            >
                                {filterError}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            sx={{ mt: 2 }}
                            onClick={apply}
                        >
                            Применить
                        </Button>
                    </Paper>

                    {reportAvailable && reportState === 'loading' && (
                        <CircularProgress
                            size={24}
                            aria-label="Загружаем отчёт"
                        />
                    )}
                    {reportAvailable && reportState === 'error' && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    startIcon={<IconRefresh size={18} />}
                                    onClick={() => setRetry((value) => value + 1)}
                                >
                                    Повторить
                                </Button>
                            }
                        >
                            Не удалось загрузить отчёт.
                        </Alert>
                    )}
                    {reportAvailable && reportState === 'forbidden' && <Alert severity="info">Доступ к отчёту закрыт.</Alert>}
                    {reportAvailable && reportState === 'unavailable' && <Alert severity="info">Отчёт ещё не подключён.</Alert>}
                    {reportAvailable && reportState === 'ready' && report && <ReportView report={report} />}
                    {journeysAvailable && (
                        <Paper component="section" elevation={0} sx={cardSx}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
                                <Box>
                                    <Typography component="h2" variant="h6" fontWeight={700}>
                                        Цепочки по аккаунтам
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Реклама, регистрация, настройка и оплата без персональных данных.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    onClick={loadJourneys}
                                    disabled={!client.getJourneys || journeysState === 'loading'}
                                >
                                    {journeysState === 'loading' ? 'Загружаем…' : 'Показать цепочки'}
                                </Button>
                            </Stack>
                            {journeysState === 'error' && (
                                <Alert severity="error">Не удалось загрузить цепочки.</Alert>
                            )}
                            {journeysState === 'ready' && journeys && (
                                <>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                        Показано {journeys.results.length} из {journeys.count_is_exact ? journeys.count : `не менее ${journeys.count}`}.
                                    </Typography>
                                    <JourneyTable report={journeys} />
                                </>
                            )}
                        </Paper>
                    )}
                </>
            )}
        </Stack>
    );
    if (embedded) {
        return <Box sx={{ maxWidth: 1280, mx: 'auto', p: { xs: 0, md: 4 } }}>{content}</Box>;
    }
    return <DesktopLayout hideRightSidebar>{content}</DesktopLayout>;
}
